import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import Header from "../../../components/common/Header";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import ImageLightbox from "../../../components/common/ImageLightbox";
import "./CollaborationDetails.css";
import { AccountRole } from "../../../components/common/AccountSidebar";

export const detailGroups = [
  { title: "Dados da coleta", fields: [
    { name: "speciesName", key: "nome_especie", label: "Nome da espécie" },
    { name: "collectionDate", key: "data", label: "Data da coleta" },
  ] },
  { title: "Localização", fields: [
    { name: "country", key: "pais", label: "País" },
    { name: "region", key: "regiao", label: "Região" },
    { name: "state", key: "estado", label: "Estado" },
    { name: "city", key: "municipio", label: "Município" },
    { name: "latitude", key: "latitude", label: "Latitude" },
    { name: "longitude", key: "longitude", label: "Longitude" },
    { name: "altitude", key: "altitude", label: "Altitude (m)" },
  ] },
  { title: "Autoria e autorizações", fields: [
    { name: "collector", key: "coletor", label: "Coletor" },
    { name: "collectorInstitution", key: "instituicao_coletor", label: "Instituição do coletor" },
    { name: "photoAuthor", key: "autor_foto", label: "Autor da foto" },
    { name: "photoAuthorInstitution", key: "instituicao_autor", label: "Instituição do autor da foto" },
    { name: "sibbio", key: "SISBIO", label: "SISBIO" },
    { name: "sisgen", key: "SISGEN", label: "SISGEN" },
  ] },
];

type DetailImage = { id: number; url: string; code?: string; status?: string };
interface Props {
  role?: AccountRole;
  id?: string;
  userName: string;
  editing?: boolean;
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
  status?: string;
  values: Record<string, string | undefined>;
  images: DetailImage[];
  comments: { id: number; title?: string; text?: string; author?: string }[];
  feedback?: React.ReactNode;
  onBack: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSubmit?: (event: React.FormEvent) => void;
  onCancel?: () => void;
}

const statusLabel = (status: string) => ({ pending: "Em análise", approved: "Aprovada", rejected: "Rejeitada", correction: "Algo a corrigir" }[status] || status);

const CollaborationDetails: React.FC<Props> = ({ role = "Admin", id, userName, editing, loading, saving, error, status, values, images, comments, feedback, onBack, onChange, onSubmit, onCancel }) => {
  const [lightbox, setLightbox] = useState<DetailImage | null>(null);
  return (
    <div className="account-profile-page account-detail-page">
      <Header />
      <div className="account-profile-layout">
        <div className="account-navigation"><Sidebar role={role} activeItem={role === "Comum" ? "minhas-colaboracoes" : "solicitacoes"} avatar={avatar} name={userName} /></div>
        <main className="account-profile-content">
          <button type="button" className="btn btn-outline-secondary detail-back" onClick={onBack}><ChevronLeft size={16} />Minhas solicitações</button>
          <div className="account-page-heading detail-heading">
            <h1>{editing ? "Editar solicitação" : "Visualizar solicitação"} #{id}</h1>
            {status && <span className={`account-status is-${status}`}>{statusLabel(status)}</span>}
          </div>
          {feedback}
          {loading && <div className="account-panel" role="status">Carregando solicitação…</div>}
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {!loading && !error && <>
            <form className="detail-sections" aria-label={editing ? "Editar solicitação" : "Dados da solicitação"} onSubmit={onSubmit || ((event) => event.preventDefault())}>
              {detailGroups.map((group, index) => (
                <section className="account-panel" key={group.title} aria-labelledby={`detail-section-${index}`}>
                  <div className="account-panel-heading"><h2 id={`detail-section-${index}`}>{group.title}</h2></div>
                  <fieldset className="account-fields" disabled={saving}>
                    {group.fields.map((field) => (
                      <div className="account-field" key={field.name}>
                        <label className="form-label" htmlFor={`detail-${field.name}`}>{field.label}</label>
                        <input id={`detail-${field.name}`} name={field.name} type={editing && field.name === "collectionDate" ? "date" : "text"} className="form-control" value={values[field.name] || (editing ? "" : "—")} onChange={onChange} readOnly={!editing} />
                      </div>
                    ))}
                  </fieldset>
                </section>
              ))}
              <section className="account-panel" aria-labelledby="detail-observations">
                <div className="account-panel-heading"><h2 id="detail-observations">Observações</h2></div>
                <textarea aria-labelledby="detail-observations" name="observations" rows={4} className="form-control" value={values.observations || (editing ? "" : "—")} readOnly={!editing} disabled={saving} onChange={onChange} />
                {editing && <div className="account-form-actions">
                  <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={onCancel}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Salvando…" : "Salvar alterações"}</button>
                </div>}
              </section>
            </form>
            <section className="account-panel detail-section" aria-labelledby="detail-images">
              <div className="account-panel-heading"><h2 id="detail-images">Imagens ({images.length})</h2></div>
              {images.length === 0 ? <p className="detail-empty">Nenhuma imagem disponível.</p> : <div className="detail-gallery">
                {images.map((image) => <article className="detail-image" key={image.id}>
                  <button type="button" aria-label={`Ampliar imagem ${image.code || image.id}`} onClick={() => setLightbox(image)}><img src={image.url} alt={image.code || "Imagem da colaboração"} loading="lazy" /></button>
                  <div><strong>{image.code || `Imagem ${image.id}`}</strong>{image.status && <span className={`account-status is-${image.status}`}>{statusLabel(image.status)}</span>}</div>
                </article>)}
              </div>}
            </section>
            <section className="account-panel detail-section" aria-labelledby="detail-comments">
              <div className="account-panel-heading"><h2 id="detail-comments">Comentários</h2></div>
              {comments.length === 0 ? <p className="detail-empty">Sem comentários.</p> : <div className="detail-comments">{comments.map((comment) => <article key={comment.id}>
                {comment.title && <h3>{comment.title}</h3>}<p>{comment.text}</p>{comment.author && <span>{comment.author}</span>}
              </article>)}</div>}
            </section>
          </>}
        </main>
      </div>
      {lightbox && <ImageLightbox src={lightbox.url} code={lightbox.code} alt="Imagem da colaboração" onClose={() => setLightbox(null)} />}
    </div>
  );
};

export default CollaborationDetails;
